import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading: authLoading } = useAuth();

  // Redirect only once the auth context itself has actually picked up the new
  // session, not immediately after signInWithPassword resolves. Navigating
  // right away was a race: the context's own session/profile state hadn't
  // updated yet, so the very next page (which checks that context) would
  // bounce back to login, making it look like sign-in needed two attempts.
  useEffect(() => {
    if (!authLoading && session) {
      const redirectTo = location.state?.from?.pathname || "/dashboard";
      navigate(redirectTo, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, authLoading]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setLoading(false);
      setError(signInError.message);
      return;
    }
    // Don't navigate here. The useEffect above handles it once the auth
    // context confirms the session is actually live.
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm border border-border rounded-lg p-8 bg-card"
      >
        <h1 className="text-xl font-semibold text-foreground mb-1">Sign in</h1>
        <p className="text-sm text-muted-foreground mb-6">
          For agency team members and clients, sign in with the email and password given to you
        </p>

        {error && (
          <p className="text-sm text-destructive mb-4 bg-destructive/10 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-1">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-1">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground rounded-md py-2 text-sm font-semibold disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}
